/**
 * The GEAR block of the loadout screen (04 §6): the four specialist items with what the
 * operation's stock holds. Goggles, cutters and the pistol are taken or left (a toggle); smoke is
 * a 0-3 stepper. It all rides on the ranger - never in a pod - and costs weight, not RP.
 * A view: it paints a state and reports presses; gear.ts holds the rules.
 */

import './gear.css';
import type { SpecialItem } from '../../../meta/types';
import { box, button, el } from '../dom';
import { stepper } from '../widgets/stepper';
import type { GearBlock, GearState, Stock } from './gear';
import { emptyGear, GEAR_ITEMS, GEAR_MAX, gearBlockText, inStock, PISTOL_ROUNDS } from './gear';
import type { LoadoutState } from './model';

export interface GearViewOptions {
  stock: Stock;
  /** Item name and description from `GameApi.describe`. */
  describe(item: SpecialItem): { name: string; text: string };
  weightOf(item: SpecialItem): number;
  /** Why one more of `item` cannot be taken now, or null. */
  blockOf(state: LoadoutState, item: SpecialItem): GearBlock;
  onStep(item: SpecialItem, delta: 1 | -1): void;
}

export interface GearView {
  readonly el: HTMLElement;
  update(state: LoadoutState): void;
}

type Paint = (gear: GearState, block: GearBlock) => void;
type Control = { el: HTMLElement; paint: Paint };
type OnStep = GearViewOptions['onStep'];

/** `TAKE` / `TAKEN`: the word changes with the state, so the green fill is never the only sign. */
function toggle(item: SpecialItem, name: string, onStep: OnStep): Control {
  let taken = false;
  const b = button('TAKE', () => onStep(item, taken ? -1 : 1), 'menu-btn-small menu-load-gear-toggle');
  return {
    el: b,
    paint(gear, block): void {
      taken = gear[item] > 0;
      const rounds = item === 'suppressed-pistol' ? ` - ${PISTOL_ROUNDS} RDS` : '';
      b.textContent = taken ? `TAKEN${rounds}` : 'TAKE';
      b.setAttribute('aria-pressed', taken ? 'true' : 'false');
      b.setAttribute('aria-label', `${taken ? 'LEAVE' : 'TAKE'} ${name}`);
      // aria-disabled, as the steppers do: a button that disables itself would drop keyboard focus.
      const blocked = !taken && block !== null;
      b.setAttribute('aria-disabled', blocked ? 'true' : 'false');
      b.classList.toggle('menu-step-off', blocked);
      b.title = blocked ? gearBlockText(block) : '';
    },
  };
}

function count(item: SpecialItem, name: string, onStep: OnStep): Control {
  const s = stepper(`${name} - THE RANGER`, (delta) => onStep(item, delta));
  return {
    el: s.el,
    paint: (gear, block) => s.set(gear[item], gear[item] > 0, block === null, gearBlockText(block)),
  };
}

export function gearView(options: GearViewOptions): GearView {
  const root = el('div', 'menu-load-gear');
  root.setAttribute('role', 'group');
  root.setAttribute('aria-label', 'SPECIALIST GEAR');
  root.append(
    box(
      'menu-load-gear-head',
      el('span', 'menu-load-box-title', 'SPECIALIST GEAR'),
      el('span', 'menu-dim menu-small', 'FROM THE OPERATION STOCK. RANGER ONLY - NEVER IN A POD. NO RP.'),
    ),
  );

  const grid = el('div', 'menu-load-gear-grid');
  const painters: Array<[item: SpecialItem, left: HTMLElement, paint: Paint]> = [];
  for (const item of GEAR_ITEMS) {
    const d = options.describe(item);
    const control = GEAR_MAX[item] > 1 ? count(item, d.name, options.onStep) : toggle(item, d.name, options.onStep);
    const name = el('span', 'menu-load-item menu-paper', d.name);
    const left = el('span', 'menu-load-gear-stock menu-small');
    const weight = el('span', 'menu-dim menu-small', `WT ${options.weightOf(item)}`);
    const line = box('menu-load-line', name, weight, left, control.el);
    line.title = d.text;
    grid.append(line);
    painters.push([item, left, control.paint]);
  }
  root.append(grid);

  return {
    el: root,
    update(state): void {
      const gear = state.gear ?? emptyGear();
      for (const [item, left, paint] of painters) {
        const remaining = inStock(options.stock, item) - gear[item];
        left.textContent = `STOCK ${Math.max(0, remaining)}`;
        left.classList.toggle('menu-dim', remaining <= 0);
        paint(gear, options.blockOf(state, item));
      }
    },
  };
}
